import React, { useEffect, useMemo, useState } from "react";
import { SegmentedMessage } from "sms-segments-calculator";
import { Paper } from "@material-ui/core";
import "./MessageSegmentPreview.css";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { GetMultipleVariablesValues } from "../../../api/variablesController";
import { ILanguage } from "../../../common/interfaces.js";
import { isEncodedChar, isTextRTL } from "./encodingChecks";

export interface MessageSegmentPreviewProps {
	messageContent: string;
	language?: ILanguage;
	index: string;
	textDirection?: string;
	messageInputFocus?: boolean;
	country: string;
	setShowBadVariableWarning?: (show: boolean) => void;
}

/**
 * the total number of segment colours
 */
const segmentColourCount = 5;

const MessageSegmentPreview: React.FC<MessageSegmentPreviewProps> = (
	props: MessageSegmentPreviewProps
) => {
	const strings = useGetComponentStringsContext().MessageSegmentPreview;

	const [segmentedMessage, setSegmentedMessage] = useState<SegmentedMessage>(
		new SegmentedMessage(props.messageContent, "auto", true)
	);

	const joinedSegmentedMessage = useMemo(
		() =>
			segmentedMessage.segments.map((segment) =>
				Array.from(segment)
					.filter(isEncodedChar)
					.map((segment) => segment.raw)
					.join("")
			),
		[segmentedMessage]
	);

	const isMessageContentRTL =
		props.language !== undefined
			? props.language.direction.toLowerCase() === "rtl"
			: isTextRTL(props.messageContent);

	useEffect(() => {
		const updateMessagePreview = (messageBodyText: string) => {
			setSegmentedMessage(new SegmentedMessage(messageBodyText, "auto", true));
		};

		const replaceAsync = async (messageBodyText: string) => {
			const variableInstances = messageBodyText.match(/{{\S+}}/g);
			if (variableInstances === null) {
				return messageBodyText;
			}
			const variableNames = variableInstances.map((name) => {
				return name.substring(2, name.length - 2);
			});

			const res = await GetMultipleVariablesValues(
				props.country,
				variableNames
			);
			if (res.responseObj) {
				variableInstances.forEach((name) => {
					const variableValue = res.responseObj?.find(
						(variable) => variable.Name === name.substring(2, name.length - 2)
					);
					if (variableValue) {
						messageBodyText = messageBodyText.replace(
							name,
							variableValue.Value
						);
					}
				});
				if (variableNames.length !== res.responseObj.length) {
					if (props.setShowBadVariableWarning)
						props.setShowBadVariableWarning(true);
				} else {
					if (props.setShowBadVariableWarning)
						props.setShowBadVariableWarning(false);
				}
				return messageBodyText;
			}
			if (props.setShowBadVariableWarning)
				props.setShowBadVariableWarning(true);
			return messageBodyText;
		};

		let messageBodyText = props.messageContent;

		// we only retrieve the variables on loss of focus from the input box to
		// reduce excessive server requests
		if (!props.messageInputFocus) {
			// get the variable values and update the preview message
			replaceAsync(messageBodyText)
				.then((message) => {
					if (message) {
						updateMessagePreview(message);
					}
				})
				.catch((err) => {
					console.log("Error retrieving variable: " + err);
				});
		} else {
			updateMessagePreview(messageBodyText);
		}
	}, [
		props.index,
		props.messageContent,
		props.textDirection,
		props.messageInputFocus,
		props.country,
		props,
	]);

	return (
		<>
			<div data-testid="messageSegmentContainer">
				<span className="label">
					<span className="first-label" data-testid="encodingType">
						<b>{strings.encodingLabel}:</b> {segmentedMessage.encodingName}
					</span>
					<span data-testid="segments">
						<b>{strings.segmentsLabel}:</b> {segmentedMessage.segments.length}
					</span>
					<span className="last-label" data-testid="totalCharacters">
						<b>{strings.charactersLabel}:</b>{" "}
						{segmentedMessage.numberOfCharacters}
					</span>
				</span>
			</div>
			<Paper
				data-encoding={segmentedMessage.encodingName}
				elevation={0}
				className="highlightedMessageContainer message-blocks"
			>
				<div
					className="preserved-whitespace message-viewer"
					style={{
						textAlign: isMessageContentRTL ? "right" : "left",
					}}
				>
					{joinedSegmentedMessage.map((section, index) => (
						<span
							className={`segment-type-${index % segmentColourCount}`}
							segment-colour={index % segmentColourCount}
							key={`${section}/${index % segmentColourCount}`}
						>
							{section}
						</span>
					))}
				</div>
			</Paper>
		</>
	);
};

export default MessageSegmentPreview;
